import classNames from 'classnames';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';

const SELECTION_KEYS = ['Enter', ' ', 'Spacebar'];
const OPEN_KEYS = ['Enter', ' ', 'Spacebar', 'ArrowDown', 'ArrowUp'];

/** Class on menus that may portal; exclude the open overlay from ClickOutside. */
export const DROPDOWN_SELECT_PORTAL_MENU_CLASS = 'DropdownSelect__menu--portal';

/** Open portaled menu — use for outside-click exclusions (closed menus stay in-tree). */
export const DROPDOWN_SELECT_PORTAL_MENU_SELECTOR = `.${DROPDOWN_SELECT_PORTAL_MENU_CLASS}.show`;

export const getMenuMaxHeight = ({
  viewportHeight,
  triggerTop,
  triggerBottom,
  padding = 5,
  heightLimit = Infinity,
}) => {
  const availableHeight = Math.max(
    triggerTop - padding,
    viewportHeight - triggerBottom - padding
  );

  return Math.max(
    0,
    Math.min(viewportHeight - padding * 2, availableHeight, heightLimit)
  );
};

const MenuElement = React.forwardRef(({ innerRef, ...props }, ref) => (
  <div
    {...props}
    ref={(element) => {
      innerRef(element);
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    }}
  />
));

MenuElement.displayName = 'DropdownSelectMenu';
MenuElement.propTypes = {
  innerRef: PropTypes.func.isRequired,
};

const limitPortalHeightModifier = {
  enabled: true,
  // Resize before preventOverflow/flip so they position the constrained menu.
  order: 250,
  padding: 5,
  fn: (data, options) => {
    const { popper, reference } = data.instance;
    const view = popper.ownerDocument.defaultView;
    const viewportHeight = Math.max(
      popper.ownerDocument.documentElement.clientHeight,
      view.innerHeight
    );
    const trigger = reference.getBoundingClientRect();
    const maxHeight = getMenuMaxHeight({
      viewportHeight,
      triggerTop: trigger.top,
      triggerBottom: trigger.bottom,
      padding: options.padding,
      heightLimit: options.maxHeight,
    });

    // Popper has already measured the natural height. Apply the limit and
    // remeasure now rather than positioning with that stale measurement.
    popper.style.maxHeight = `${maxHeight}px`;
    popper.style.overflowY = 'auto';
    const previousHeight = data.offsets.popper.height;
    data.offsets.popper.height = popper.offsetHeight;
    if (data.placement.startsWith('top')) {
      data.offsets.popper.top += previousHeight - data.offsets.popper.height;
    }
    data.offsets.popper.bottom =
      data.offsets.popper.top + data.offsets.popper.height;
    data.styles.maxHeight = `${maxHeight}px`;
    data.styles.overflowY = 'auto';
    return data;
  },
};

const matchReferenceWidthModifier = {
  enabled: true,
  order: 840,
  fn: (data) => {
    const width = data.offsets.reference.width;
    data.styles.width = `${width}px`;
    data.styles.minWidth = `${width}px`;
    return data;
  },
};

@observer
class DropdownSelect extends Component {
  toggleRef = React.createRef();

  pendingCloseReason = null;

  menuElement = null;

  setMenuElement = (element) => {
    this.menuElement = element;
  };

  focusToggle = () => {
    const toggle = this.toggleRef.current;
    if (toggle && typeof toggle.focus === 'function') {
      toggle.focus();
      return true;
    }

    return false;
  };

  handleMenuClickCapture = (event) => {
    const item = event?.target?.closest?.('.dropdown-item');
    if (!item || item.classList.contains('disabled')) {
      return;
    }

    this.pendingCloseReason = 'selection';
  };

  handleMenuKeyDownCapture = (event) => {
    const item = event?.target?.closest?.('.dropdown-item');
    if (!item || item.classList.contains('disabled')) {
      return;
    }

    if (SELECTION_KEYS.includes(event.key)) {
      this.pendingCloseReason = 'selection';
      return;
    }

    if (event.key === 'Escape') {
      this.pendingCloseReason = 'dismiss';
      return;
    }

    if (event.key === 'Tab') {
      this.pendingCloseReason = null;
    }
  };

  handleToggle = (event, isOpen) => {
    const { focusSelectedItemOnKeyboardOpen, focusToggleOnClose } = this.props;

    const isKeyboardOpenTrigger =
      event?.type === 'keydown' && OPEN_KEYS.includes(event.key);
    if (focusSelectedItemOnKeyboardOpen && isOpen && isKeyboardOpenTrigger) {
      setTimeout(() => {
        const menu = this.menuElement;
        const selectedItem =
          menu?.querySelector('.dropdown-item[aria-current="true"]') ||
          menu?.querySelector('.dropdown-item.active');
        const fallbackItem = menu
          ? Array.from(
              menu.querySelectorAll('.dropdown-item:not(.disabled)')
            )[0]
          : undefined;
        const itemToFocus = selectedItem || fallbackItem;
        if (itemToFocus && typeof itemToFocus.focus === 'function') {
          itemToFocus.focus();
        }
      }, 0);
    }

    const shouldRestoreFocus =
      !isOpen &&
      focusToggleOnClose &&
      ['selection', 'dismiss'].includes(this.pendingCloseReason);
    if (shouldRestoreFocus) {
      setTimeout(() => {
        this.focusToggle();
      }, 0);
    }

    if (!isOpen) {
      this.pendingCloseReason = null;
    }

    if (typeof this.props.onToggle === 'function') {
      this.props.onToggle(event, isOpen);
    }
  };

  render() {
    const {
      label,
      children,
      disabled,
      isLarge,
      className,
      focusSelectedItemOnKeyboardOpen,
      focusToggleOnClose,
      onToggle,
      toggleClassName,
      toggleNav,
      caret = true,
      compactLabel = false,
      positionFixed = false,
      menuContainer,
      menuClassName,
      menuModifiers,
      ...props
    } = this.props;

    const styles =
      isLarge && !menuContainer
        ? {
            overflowY: 'scroll',
            maxHeight: '500px',
          }
        : {};

    const modifiers = positionFixed
      ? {
          matchReferenceWidth: matchReferenceWidthModifier,
          preventOverflow: {
            boundariesElement: 'viewport',
          },
          ...(menuContainer && {
            limitPortalHeight: {
              ...limitPortalHeightModifier,
              maxHeight: isLarge ? 500 : Infinity,
            },
          }),
          ...menuModifiers,
        }
      : menuModifiers;

    const menuClasses = classNames(
      'DropdownSelect__menu',
      menuClassName,
      menuContainer && DROPDOWN_SELECT_PORTAL_MENU_CLASS
    );

    return (
      <UncontrolledDropdown
        className={classNames('DropdownSelect', className, {
          'DropdownSelect--position-fixed': positionFixed,
        })}
        disabled={disabled}
        onToggle={this.handleToggle}
        {...props}
      >
        <DropdownToggle
          caret={false}
          nav={toggleNav}
          disabled={disabled}
          innerRef={this.toggleRef}
          className={classNames('dropdown-toggle', toggleClassName, {
            'DropdownSelect__toggle--no-caret': !caret,
          })}
        >
          <span
            className={classNames('DropdownSelect__label', {
              'DropdownSelect__label--compact': compactLabel,
            })}
          >
            {label}
          </span>
        </DropdownToggle>
        <DropdownMenu
          tag={MenuElement}
          innerRef={this.setMenuElement}
          className={menuClasses}
          style={styles}
          positionFixed={positionFixed}
          container={menuContainer}
          modifiers={modifiers}
          onClickCapture={this.handleMenuClickCapture}
          onKeyDownCapture={this.handleMenuKeyDownCapture}
        >
          {children}
        </DropdownMenu>
      </UncontrolledDropdown>
    );
  }
}

DropdownSelect.propTypes = {
  label: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
  isLarge: PropTypes.bool,
  className: PropTypes.string,
  focusSelectedItemOnKeyboardOpen: PropTypes.bool,
  focusToggleOnClose: PropTypes.bool,
  onToggle: PropTypes.func,
  toggleClassName: PropTypes.string,
  toggleNav: PropTypes.bool,
  caret: PropTypes.bool,
  compactLabel: PropTypes.bool,
  positionFixed: PropTypes.bool,
  menuContainer: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.func,
  ]),
  menuClassName: PropTypes.string,
  menuModifiers: PropTypes.object,
};

DropdownSelect.defaultProps = {
  isLarge: false,
  focusSelectedItemOnKeyboardOpen: false,
  focusToggleOnClose: false,
  onToggle: undefined,
  toggleClassName: undefined,
  toggleNav: false,
  compactLabel: false,
  positionFixed: false,
  menuContainer: undefined,
  menuClassName: undefined,
  menuModifiers: undefined,
};

export default DropdownSelect;
